import {
  Button, Card,
  CardHeader, FormControl, InputLabel, MenuItem, Pagination, Select,
  Stack, Switch, Typography
} from '@mui/material';
import {
  DataGrid, GridCellEditStopReasons, gridPageCountSelector,
  gridPageSelector,
  koKR,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid';
import { Fragment, useEffect, useState } from 'react';
import { apiService } from '../../api/axiosClient';
import Spinner from '../../components/spinner';
import CustomNoRowsOverlay from '../../utils/CustomNoRowsOverlay';
import CategoryToolbar from './CategoryToolbar';
import DialogCategory from './DialogCategory';

const TableCategory = (props) => {
  const [loading, setLoading] = useState(true)
  const { reload, setReload } = props
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState(0)
  const [sort, setSort] = useState('asc')
  const [pageSize, setPageSize] = useState(10)
  const [list, setList] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const [sortColumn, setSortColumn] = useState('level1')
  const [selected, setSelected] = useState([])
  const [show, setShow] = useState(false)
  const [level, setLevel] = useState('')
  const [name, setName] = useState({})

  const loadCategory = 
    async (sort, query, column) => {
      try {
        const response = await apiService.get('/category', {
          params: {
            query,
            sort,
            column,
            page,
            pageSize
          }
        });
        const payload = response.data;
        setTotal(payload.size);
        setList(payload.list);
        setLoading(false);
      } catch (e) {
        console.error('Error loading category data:', e);
      }
    }

  useEffect(() => {
    loadCategory(sort, searchValue, sortColumn)
  }, [page, pageSize, reload])

  useEffect(() => {
    if (level) {
      setShow(true)
    }
  }, [name, level])

  const handleSortModel = (newModel) => {
    if (newModel.length) {
      setSort(newModel[0].sort)
      setSortColumn(newModel[0].field)
      loadCategory(newModel[0].sort, searchValue, newModel[0].field)
    } else {
      setSort('asc')
      setSortColumn('level1')
    }
  }

  const handleSearch = value => {
    setSearchValue(value)
    loadCategory(sort, value, sortColumn)
  }

  const onPageSizeChange = e => {
    setPageSize(e.target.value)
  }

  const handleCheck = (id, checked) => {
    const updatedList = list.filter(item => {
      if (item.id === id) {
        item.ca_use = checked
      }
      return item
    });

    setList(updatedList)
  }

  const columns = [
    {
      flex: 0.1,
      minWidth: 100,
      field: 'level1',
      headerName: '1차 카테고리',
      editable: true,
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.level1}
        </Typography>
      )
    },
    {
      flex: 0.25,
      minWidth: 100,
      field: 'level2',
      headerName: '2차 카테고리',
      editable: true,
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.level2}
        </Typography>
      )
    },
    {
      flex: 0.25,
      field: 'level3',
      minWidth: 100,
      headerName: '3차 카테고리',
      editable: true,
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.level3}
        </Typography>
      )
    },
    {
      flex: 0.1,
      field: 'ca_use',
      minWidth: 50,
      headerName: '노출',
      renderCell: params => (
        <Switch
          checked={params.row.ca_use}
          onChange={(e) => {
            handleCheck(params.row.id, e.target.checked)
          }}
          inputProps={{ 'aria-label': 'controlled' }}
        />
      )
    },
    {
      flex: 0.2,
      minWidth: 160,
      field: 'actions',
      headerName: '',
      // eslint-disable-next-line arrow-body-style
      renderCell: params => {
        return (
          params.row.level !== 'l3' ? (
            <Button size='small' variant='outlined' color='primary' onClick={e => {
              e.preventDefault()

              if (params.row.level === 'l1') {
                setName({ "level1": params.row.level1, "level2": "" })
              }
              else if (params.row.level === 'l2') {
                setName({ "level1": params.row.level1, "level2": params.row.level2 })
              }

              setLevel(params.row.level);
            }}>
              {params.row.level === 'l1' ? '+ 2차 카테고리' : '+ 3차 카테고리'}
            </Button>
          ) : null
        )
      }
    }
  ]

  function CustomPagination() {
    const apiRef = useGridApiContext();
    const _page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Stack direction="row" spacing={2} sx={{ pr: 4, alignItems: 'center' }}>
        <InputLabel id="pagesize-select">행/페이지</InputLabel>
        <FormControl variant="standard" sx={{ mr: 1, minWidth: 70 }}>
          <Select
            labelId="pagesize-select"
            id="pagesize-select"
            value={pageSize}
            onChange={onPageSizeChange}
            label="pageSize"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
        <Pagination
          color="primary"
          count={pageCount}
          page={_page + 1}
          onChange={(event, value) => apiRef.current.setPage(value - 1)}
        />
      </Stack>
    );
  }

  return (
    loading ?
    <Spinner/>
    :
    <Fragment>
      <Card>
        <CardHeader title='카테고리' />
        <DataGrid
          sx={{
            ...(list.length == 0 && { '& .MuiDataGrid-virtualScrollerContent': {height: '200px !important' } })
          }}
          localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
          autoHeight
          pagination
          rows={list}
          rowCount={total || 0}
          columns={columns}
          pageSize={pageSize}
          sortingMode='server'
          paginationMode='server'
          onSortModelChange={handleSortModel}
          onPageChange={newPage => setPage(newPage)}
          components={{
            Toolbar: CategoryToolbar,
            Pagination: CustomPagination,
            NoRowsOverlay: CustomNoRowsOverlay,
          }}
          componentsProps={{
            toolbar: {
              value: searchValue,
              clearSearch: () => handleSearch(''),
              onChange: event => handleSearch(event.target.value),
              numSelected: selected.length,
              selected,
              setList,
              setReload
            }
          }}
          checkboxSelection
          disableSelectionOnClick
          onSelectionModelChange={(ids) => {
            const selectedIDs = new Set(ids);
            const selectedRows = list.filter((row) =>
              selectedIDs.has(row.id),
            );

            setSelected(selectedRows)
          }}
          experimentalFeatures={{ newEditingApi: true }}
          onCellEditStop={(params, event) => {
            if (params.reason === GridCellEditStopReasons.enterKeyDown) {
              // event.defaultMuiPrevented = true;            
              if (event.target.value?.length === 0) {
                return
              }

              // change source data
              setList(list.filter(item => {
                if (item.id === params.id) {
                  item[params.field] = event.target.value
                }

                return item
              }));
            }
          }}
        />
      </Card>
      <DialogCategory show={show} setShow={setShow} level={level} name={name} setReload={setReload} />
    </Fragment>
  )
}

export default TableCategory
